import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useGiftees } from "../hooks/useGiftees";
import { useReminders } from "../hooks/useReminders";
import { useAuth } from "../../../hooks/useAuth";
import { db } from "../../../firebase.js";
import styles from "../styles/GifteeProfile.module.css";
import wizardStyles from "../styles/AddGifteeWizard.module.css";
import GifteeAppBottomNav from "../components/GifteeAppBottomNav.jsx";
import GifteeBudgetRangeSlider from "../components/GifteeBudgetRangeSlider.jsx";
import GifteeInterestsMoreSheet from "../components/GifteeInterestsMoreSheet.jsx";
import GifteeHolidaysMoreSheet from "../components/GifteeHolidaysMoreSheet.jsx";
import GifteeCustomOccasionSheet from "../components/GifteeCustomOccasionSheet.jsx";
import {
  US_HOLIDAY_PRESET_IDS,
  getNextUsHolidayOccurrence,
  getUsHolidayOccasions,
} from "../data/usHolidayPresets.js";
import {
  reminderTimestampAndRepeat,
} from "../utils/celebrationDateInput.js";
import {
  DEFAULT_GIFTEE_BUDGET_PAIR,
  budgetPairReducer,
} from "../utils/budgetPair.js";
import {
  customInterestsFromSet,
  orderedSelectedPresetInterests,
  parseInterestsFromGiftee,
} from "../utils/gifteeInterests.js";

/** Same presets and default order as AddGifteeWizard `RELATIONSHIPS`. */
const RELATIONSHIPS = [
  "Mom",
  "Dad",
  "Friend",
  "Partner / Spouse",
  "Child",
  "Sibling",
  "Brother",
  "Sister",
  "Coworker",
  "Grandparent",
];

function reminderTsToMmdd(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}`;
}

function splitGifteeName(giftee) {
  const rawFirst = (giftee?.firstName || "").trim();
  const rawLast = (giftee?.lastName || "").trim();
  if (rawFirst || rawLast) return { firstName: rawFirst, lastName: rawLast };
  const full = (giftee?.name || "").trim();
  if (!full) return { firstName: "", lastName: "" };
  const parts = full.split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

/** Match AddGifteeWizard formatting for slider labels ($200+ cap label). */
function fmtBudgetDollar(v) {
  return v >= 200 ? "$200+" : `$${v}`;
}

/** Copy for delete confirmation — Figma `557:11398` (dynamic name). */
function deleteConfirmHeadline(firstName, combinedNameFallback) {
  const fn = (firstName || "").trim();
  if (fn) return `Are you sure you want to delete ${fn}'s profile?`;
  const full = (combinedNameFallback || "").trim();
  if (full) return `Are you sure you want to delete ${full}'s profile?`;
  return "Are you sure you want to delete this profile?";
}

export default function GifteeProfileScreen() {
  const { gifteeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { giftees, loading: gifteesLoading } = useGiftees();
  const { reminders, loading: remindersLoading } = useReminders(gifteeId);

  const giftee = giftees.find((g) => g.id === gifteeId);

  /** Must run before any early return — same hook order on every render. */
  const customInterests = useMemo(() => {
    if (!giftee) return [];
    return customInterestsFromSet(parseInterestsFromGiftee(giftee));
  }, [giftee]);

  const [budgetPair, dispatchBudget] = useReducer(budgetPairReducer, DEFAULT_GIFTEE_BUDGET_PAIR);

  const hydratedGifteeRef = useRef(null);
  const budgetTouchRef = useRef(false);
  const persistTimerRef = useRef(null);

  useEffect(() => {
    hydratedGifteeRef.current = null;
    budgetTouchRef.current = false;
    dispatchBudget({ type: "hydrate", budgetMin: undefined, budgetMax: undefined });
  }, [gifteeId]);

  useEffect(() => {
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    if (hydratedGifteeRef.current === gifteeId) return;
    hydratedGifteeRef.current = gifteeId;
    dispatchBudget({
      type: "hydrate",
      budgetMin: g.budgetMin,
      budgetMax: g.budgetMax,
    });
  }, [gifteeId, giftees]);

  useEffect(() => {
    if (!budgetTouchRef.current || !gifteeId || !currentUser?.uid) return undefined;
    clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(async () => {
      try {
        const { updateDoc, doc, serverTimestamp } = await import("firebase/firestore");
        await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
          budgetMin: budgetPair.min,
          budgetMax: budgetPair.max,
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error("GifteeProfileScreen budget persist:", e);
      }
    }, 400);
    return () => clearTimeout(persistTimerRef.current);
  }, [budgetPair.min, budgetPair.max, gifteeId, currentUser?.uid]);

  const [relationshipValue, setRelationshipValue] = useState("");

  useEffect(() => {
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) {
      setRelationshipValue("");
      return;
    }
    setRelationshipValue((g.relationship || "").trim());
  }, [gifteeId, giftees]);

  const [interestsMoreOpen, setInterestsMoreOpen] = useState(false);
  const [interestsDraft, setInterestsDraft] = useState(() => new Set());
  const [customInterestOpen, setCustomInterestOpen] = useState(false);
  const [customInterestText, setCustomInterestText] = useState("");
  const [customOccasionSheetOpen, setCustomOccasionSheetOpen] =
    useState(false);
  const [customOccasionEditId, setCustomOccasionEditId] = useState(null);
  const [holidaysMoreOpen, setHolidaysMoreOpen] = useState(false);
  const [holidaysDraft, setHolidaysDraft] = useState(() => new Set());
  /** Delete-account confirmation (UI only — no deletion wired yet). */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const holidayOccasions = getUsHolidayOccasions();

  /** `null` = read-only for both; `'first' | 'last'` = that row is an input until confirmed. */
  const [nameEditField, setNameEditField] = useState(null);
  const [firstNameDraft, setFirstNameDraft] = useState("");
  const [lastNameDraft, setLastNameDraft] = useState("");
  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);
  const celebrationsSectionRef = useRef(null);
  const celebrationsScrollDoneRef = useRef(false);

  useEffect(() => {
    celebrationsScrollDoneRef.current = false;
  }, [gifteeId]);

  useEffect(() => {
    if (gifteesLoading || remindersLoading || !giftee) return undefined;
    if (searchParams.get("section") !== "celebrations") return undefined;
    if (celebrationsScrollDoneRef.current) return undefined;

    const scrollToCelebrations = () => {
      const el = celebrationsSectionRef.current;
      if (!el) return false;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      celebrationsScrollDoneRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("section");
          return next;
        },
        { replace: true },
      );
      return true;
    };

    const raf = window.requestAnimationFrame(() => {
      if (!scrollToCelebrations()) {
        window.setTimeout(scrollToCelebrations, 0);
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    gifteesLoading,
    remindersLoading,
    giftee,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    setInterestsMoreOpen(false);
    setCustomInterestOpen(false);
    setCustomInterestText("");
    setCustomOccasionSheetOpen(false);
    setCustomOccasionEditId(null);
    setHolidaysMoreOpen(false);
    setDeleteConfirmOpen(false);
  }, [gifteeId]);

  useEffect(() => {
    if (!deleteConfirmOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [deleteConfirmOpen]);

  useEffect(() => {
    if (!deleteConfirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDeleteConfirmOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteConfirmOpen]);

  useEffect(() => {
    setNameEditField(null);
    setFirstNameDraft("");
    setLastNameDraft("");
  }, [gifteeId]);

  useEffect(() => {
    if (nameEditField !== "first" && nameEditField !== "last") return undefined;
    const t = window.requestAnimationFrame(() => {
      const el =
        nameEditField === "first"
          ? firstNameInputRef.current
          : lastNameInputRef.current;
      el?.focus();
      if (typeof el?.select === "function") el.select();
    });
    return () => window.cancelAnimationFrame(t);
  }, [nameEditField]);

  const persistRelationship = async (value) => {
    if (!gifteeId || !currentUser?.uid) return;
    setRelationshipValue(value);
    try {
      const { updateDoc, doc, serverTimestamp } = await import("firebase/firestore");
      await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
        relationship: value,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("GifteeProfileScreen relationship persist:", e);
      const g = giftees.find((x) => x.id === gifteeId);
      setRelationshipValue(g ? (g.relationship || "").trim() : "");
    }
  };

  const openInterestsMore = () => {
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    setInterestsDraft(new Set(parseInterestsFromGiftee(g)));
    setInterestsMoreOpen(true);
  };

  const toggleDraftInterestChip = (chip) => {
    setInterestsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });
  };

  const saveInterestsMore = async () => {
    if (!gifteeId || !currentUser?.uid) return;
    try {
      const { updateDoc, doc, serverTimestamp } = await import("firebase/firestore");
      await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
        interests: [...interestsDraft],
        updatedAt: serverTimestamp(),
      });
      setInterestsMoreOpen(false);
    } catch (e) {
      console.error("GifteeProfileScreen interests persist:", e);
    }
  };

  const saveCustomInterest = async () => {
    const trimmed = customInterestText.trim();
    if (!trimmed || !gifteeId || !currentUser?.uid) return;
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    const current = parseInterestsFromGiftee(g);
    if (!current.has(trimmed)) {
      current.add(trimmed);
      try {
        const { updateDoc, doc, serverTimestamp } = await import("firebase/firestore");
        await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
          interests: [...current],
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error("GifteeProfileScreen custom interest save:", e);
        return;
      }
    }
    setCustomInterestText("");
    setCustomInterestOpen(false);
  };

  const openHolidaysMore = () => {
    const current = new Set(
      reminders
        .filter((r) => US_HOLIDAY_PRESET_IDS.has(r.type))
        .map((r) => r.type),
    );
    setHolidaysDraft(current);
    setHolidaysMoreOpen(true);
  };

  const toggleHolidayDraft = (holidayPresetId) => {
    setHolidaysDraft((prev) => {
      const next = new Set(prev);
      if (next.has(holidayPresetId)) {
        if (next.size <= 1) return prev;
        next.delete(holidayPresetId);
      } else {
        next.add(holidayPresetId);
      }
      return next;
    });
  };

  const saveHolidaysMore = async () => {
    if (!gifteeId || !currentUser?.uid) return;
    const uid = currentUser.uid;
    if (holidaysDraft.size < 1) return;
    try {
      const {
        addDoc,
        collection,
        deleteDoc,
        doc,
        increment,
        serverTimestamp,
        Timestamp,
        updateDoc,
      } = await import("firebase/firestore");
      const gifteeDoc = doc(db, "users", uid, "giftees", gifteeId);
      const remindersCol = collection(
        db,
        "users",
        uid,
        "giftees",
        gifteeId,
        "reminders",
      );

      const remindersByPresetType = new Map(
        reminders
          .filter((r) => US_HOLIDAY_PRESET_IDS.has(r.type))
          .map((r) => [r.type, r.id]),
      );

      let netReminderDelta = 0;

      for (const occ of holidayOccasions) {
        const want = holidaysDraft.has(occ.id);
        const reminderId = remindersByPresetType.get(occ.id);
        if (want && !reminderId) {
          const reminderDate = getNextUsHolidayOccurrence(occ.id);
          await addDoc(remindersCol, {
            userId: uid,
            gifteeId,
            type: occ.id,
            title: occ.name,
            date: Timestamp.fromDate(reminderDate),
            repeat: "yearly",
            budget: null,
            notes: null,
            active: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          netReminderDelta += 1;
        } else if (!want && reminderId) {
          await deleteDoc(
            doc(db, "users", uid, "giftees", gifteeId, "reminders", reminderId),
          );
          netReminderDelta -= 1;
        }
      }

      if (netReminderDelta !== 0) {
        await updateDoc(gifteeDoc, {
          reminderCount: increment(netReminderDelta),
          updatedAt: serverTimestamp(),
        });
      }

      setHolidaysMoreOpen(false);
    } catch (e) {
      console.error("GifteeProfileScreen holidays persist:", e);
    }
  };

  /** Remove one preset holiday from profile — blocked when only one preset holiday remains. */
  const removeProfileHolidayPreset = async (presetId) => {
    if (!gifteeId || !currentUser?.uid) return;
    const presetReminders = reminders.filter((r) =>
      US_HOLIDAY_PRESET_IDS.has(r.type),
    );
    if (presetReminders.length <= 1) return;
    const target = presetReminders.find((r) => r.type === presetId);
    if (!target) return;
    try {
      const { deleteDoc, doc, increment, serverTimestamp, updateDoc } =
        await import("firebase/firestore");
      await deleteDoc(
        doc(
          db,
          "users",
          currentUser.uid,
          "giftees",
          gifteeId,
          "reminders",
          target.id,
        ),
      );
      await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
        reminderCount: increment(-1),
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("GifteeProfileScreen remove holiday preset:", e);
    }
  };

  const removeProfileInterest = async (label) => {
    if (!gifteeId || !currentUser?.uid) return;
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    const current = parseInterestsFromGiftee(g);
    if (current.size <= 1) return;
    if (!current.has(label)) return;
    current.delete(label);
    try {
      const { updateDoc, doc, serverTimestamp } = await import("firebase/firestore");
      await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
        interests: [...current],
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("GifteeProfileScreen remove interest:", e);
    }
  };

  const closeCustomOccasionSheet = () => {
    setCustomOccasionSheetOpen(false);
    setCustomOccasionEditId(null);
  };

  const editingCustomReminder = useMemo(() => {
    if (customOccasionEditId == null) return undefined;
    return reminders.find(
      (r) => r.id === customOccasionEditId && r.type === "custom",
    );
  }, [reminders, customOccasionEditId]);

  const saveProfileCustomOccasion = async ({ title, mmddyy, annual }) => {
    if (!gifteeId || !currentUser?.uid) return;
    const uid = currentUser.uid;
    const planned = reminderTimestampAndRepeat(mmddyy, annual);
    try {
      const {
        addDoc,
        collection,
        doc,
        increment,
        serverTimestamp,
        updateDoc,
      } = await import("firebase/firestore");
      const gifteeDoc = doc(db, "users", uid, "giftees", gifteeId);
      const remindersCol = collection(
        db,
        "users",
        uid,
        "giftees",
        gifteeId,
        "reminders",
      );

      if (customOccasionEditId) {
        await updateDoc(
          doc(
            db,
            "users",
            uid,
            "giftees",
            gifteeId,
            "reminders",
            customOccasionEditId,
          ),
          {
            title,
            date: planned.timestamp,
            repeat: planned.repeat,
            updatedAt: serverTimestamp(),
          },
        );
      } else {
        await addDoc(remindersCol, {
          userId: uid,
          gifteeId,
          type: "custom",
          title,
          date: planned.timestamp,
          repeat: planned.repeat,
          budget: null,
          notes: null,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await updateDoc(gifteeDoc, {
          reminderCount: increment(1),
          updatedAt: serverTimestamp(),
        });
      }
      closeCustomOccasionSheet();
    } catch (e) {
      console.error("GifteeProfileScreen custom occasion save:", e);
    }
  };

  const deleteProfileCustomReminder = async () => {
    if (!gifteeId || !currentUser?.uid || !customOccasionEditId) return;
    const uid = currentUser.uid;
    try {
      const { deleteDoc, doc, increment, serverTimestamp, updateDoc } =
        await import("firebase/firestore");
      await deleteDoc(
        doc(
          db,
          "users",
          uid,
          "giftees",
          gifteeId,
          "reminders",
          customOccasionEditId,
        ),
      );
      await updateDoc(doc(db, "users", uid, "giftees", gifteeId), {
        reminderCount: increment(-1),
        updatedAt: serverTimestamp(),
      });
      closeCustomOccasionSheet();
    } catch (e) {
      console.error("GifteeProfileScreen custom occasion delete:", e);
    }
  };

  const beginNameEdit = (field) => {
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    const { firstName: fn, lastName: ln } = splitGifteeName(g);
    if (field === "first") {
      setFirstNameDraft(fn);
      setNameEditField("first");
    } else {
      setLastNameDraft(ln);
      setNameEditField("last");
    }
  };

  const commitNameEdit = async (field) => {
    if (!gifteeId || !currentUser?.uid) return;
    const g = giftees.find((x) => x.id === gifteeId);
    if (!g) return;
    const { firstName: baseFirst, lastName: baseLast } = splitGifteeName(g);
    const nextFirst = field === "first" ? firstNameDraft.trim() : baseFirst;
    const nextLast = field === "last" ? lastNameDraft.trim() : baseLast;
    const combinedName = [nextFirst, nextLast].filter(Boolean).join(" ").trim();
    try {
      const { updateDoc, doc, serverTimestamp } = await import("firebase/firestore");
      await updateDoc(doc(db, "users", currentUser.uid, "giftees", gifteeId), {
        firstName: nextFirst,
        lastName: nextLast,
        name: combinedName,
        updatedAt: serverTimestamp(),
      });
      setNameEditField(null);
    } catch (e) {
      console.error("GifteeProfileScreen name persist:", e);
    }
  };

  const onNameActionClick = (field) => {
    if (nameEditField === field) {
      void commitNameEdit(field);
    } else {
      beginNameEdit(field);
    }
  };

  /**
   * End edit mode without saving when focus leaves the input (tap/click elsewhere).
   * Skips cancellation when focus moves to a name-field action button (save / other row pencil)
   * so the check control still commits; pointerdown preventDefault on the check avoids blur-before-click on tap.
   */
  const cancelNameEditOnBlur = (field) => (e) => {
    if (nameEditField !== field) return;
    const next = e.relatedTarget;
    if (
      next &&
      typeof next.closest === "function" &&
      next.closest("[data-profile-name-action]")
    ) {
      return;
    }
    window.setTimeout(() => {
      setNameEditField((cur) => {
        if (cur !== field) return cur;
        const active = document.activeElement;
        if (
          active &&
          typeof active.closest === "function" &&
          active.closest("[data-profile-name-action]")
        ) {
          return cur;
        }
        return null;
      });
    }, 0);
  };

  if (gifteesLoading || remindersLoading) {
    return (
      <div className={styles.screen}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>Edit Profile</h1>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => navigate(-1)}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <p className={styles.loading}>Loading...</p>
      </div>
    );
  }

  if (!giftee) {
    return (
      <div className={styles.screen}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>Edit Profile</h1>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => navigate(-1)}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <p className={styles.notFound}>Giftee not found.</p>
      </div>
    );
  }

  const { firstName, lastName } = splitGifteeName(giftee);
  const interests = parseInterestsFromGiftee(giftee);
  const relationship = relationshipValue.trim();
  const selectedRelationshipFirst = relationship
    ? [
        relationship,
        ...RELATIONSHIPS.filter((item) => item !== relationship),
      ]
    : RELATIONSHIPS;
  const selectedPresetInterests = orderedSelectedPresetInterests(interests);
  /** Profile view: only interests stored on this giftee (presets + custom), not the full picker list. */
  const profileInterestChips = [...selectedPresetInterests, ...customInterests];
  const canRemoveAnyInterest = profileInterestChips.length > 1;
  const attachedHolidayTypeSet = new Set(
    reminders
      .filter((r) => US_HOLIDAY_PRESET_IDS.has(r.type))
      .map((r) => r.type)
  );
  const attachedHolidayPresets = holidayOccasions.filter((p) =>
    attachedHolidayTypeSet.has(p.id)
  );
  const canRemoveAnyHoliday = attachedHolidayPresets.length > 1;
  const customOccasions = reminders
    .filter((r) => r.type === "custom")
    .map((r) => ({
      id: r.id,
      title: (r.title || "").trim(),
      date: reminderTsToMmdd(r.date),
    }))
    .filter((r) => r.title);

  return (
    <div className={styles.screen}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Edit Profile</h1>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={() => navigate(-1)}
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      <div className={styles.scroll}>
        <div className={styles.sectionFields}>
          <div className={styles.fieldGroup}>
            <h2 className={styles.sectionLabel}>First Name</h2>
            <div className={styles.formField}>
              {nameEditField === "first" ? (
                <input
                  ref={firstNameInputRef}
                  id="giftee-profile-first-name"
                  className={styles.formFieldInput}
                  type="text"
                  autoComplete="given-name"
                  value={firstNameDraft}
                  onChange={(e) => setFirstNameDraft(e.target.value)}
                  onBlur={cancelNameEditOnBlur("first")}
                  aria-label="First name"
                />
              ) : (
                <span className={styles.formFieldValue}>
                  {firstName || "—"}
                </span>
              )}
              <button
                type="button"
                className={styles.formFieldAction}
                data-profile-name-action
                aria-label={
                  nameEditField === "first"
                    ? "Save first name"
                    : "Edit first name"
                }
                onPointerDown={(e) => {
                  if (nameEditField === "first") e.preventDefault();
                }}
                onClick={() => onNameActionClick("first")}
              >
                <i
                  className={
                    nameEditField === "first"
                      ? "fa-solid fa-check"
                      : "fa-regular fa-pen-to-square"
                  }
                  aria-hidden
                />
              </button>
            </div>
          </div>
          <div className={styles.fieldGroup}>
            <h2 className={styles.sectionLabel}>Last Name</h2>
            <div className={styles.formField}>
              {nameEditField === "last" ? (
                <input
                  ref={lastNameInputRef}
                  id="giftee-profile-last-name"
                  className={styles.formFieldInput}
                  type="text"
                  autoComplete="family-name"
                  value={lastNameDraft}
                  onChange={(e) => setLastNameDraft(e.target.value)}
                  onBlur={cancelNameEditOnBlur("last")}
                  aria-label="Last name"
                />
              ) : (
                <span className={styles.formFieldValue}>
                  {lastName || "—"}
                </span>
              )}
              <button
                type="button"
                className={styles.formFieldAction}
                data-profile-name-action
                aria-label={
                  nameEditField === "last" ? "Save last name" : "Edit last name"
                }
                onPointerDown={(e) => {
                  if (nameEditField === "last") e.preventDefault();
                }}
                onClick={() => onNameActionClick("last")}
              >
                <i
                  className={
                    nameEditField === "last"
                      ? "fa-solid fa-check"
                      : "fa-regular fa-pen-to-square"
                  }
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>

        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>Relationship</h2>
          <div className={styles.pillWrap}>
            {selectedRelationshipFirst.map((item) => (
              <button
                key={item}
                type="button"
                className={`${styles.pill} ${
                  relationship === item ? styles.pillSelected : ""
                }`}
                onClick={() => persistRelationship(item)}
                aria-pressed={relationship === item}
              >
                {item}
              </button>
            ))}
            <span className={styles.pill}>
              <i className="fa-solid fa-plus" aria-hidden />
              Custom
            </span>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>Interests</h2>
          <div className={styles.pillWrap}>
            {profileInterestChips.map((item) => (
              <button
                key={item}
                type="button"
                className={`${styles.pill} ${styles.pillSelected}`}
                disabled={!canRemoveAnyInterest}
                title={
                  canRemoveAnyInterest
                    ? "Remove interest"
                    : "At least one interest is required"
                }
                onClick={() => removeProfileInterest(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              className={styles.pill}
              onClick={openInterestsMore}
            >
              <i className="fa-solid fa-plus" aria-hidden />
              More
            </button>
            <button
              type="button"
              className={styles.pill}
              onClick={() => setCustomInterestOpen(true)}
            >
              <i className="fa-solid fa-plus" aria-hidden />
              Custom
            </button>
          </div>
        </section>

        <section className={styles.budgetSection}>
          <h2 className={styles.sectionLabel}>Budget</h2>
          <div className={styles.budgetSliderWrap}>
            <GifteeBudgetRangeSlider
              budgetMin={budgetPair.min}
              budgetMax={budgetPair.max}
              onMinChange={(v) => {
                budgetTouchRef.current = true;
                dispatchBudget({ type: "setMin", value: v });
              }}
              onMaxChange={(v) => {
                budgetTouchRef.current = true;
                dispatchBudget({ type: "setMax", value: v });
              }}
              fmtBudget={fmtBudgetDollar}
            />
          </div>
        </section>

        <section
          ref={celebrationsSectionRef}
          className={styles.celebrationsSection}
          aria-labelledby="giftee-profile-celebrations-heading"
        >
          <h2 id="giftee-profile-celebrations-heading" className={styles.sectionLabel}>
            Celebrations
          </h2>

          <div className={styles.celebrationGroup}>
            <h3 className={styles.groupLabel}>Custom Occasions (Birthdays, Graduations, ect.)</h3>
            <div className={styles.celebrationPills}>
              {customOccasions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.pill} ${styles.pillSelected}`}
                  onClick={() => {
                    setCustomOccasionEditId(item.id);
                    setCustomOccasionSheetOpen(true);
                  }}
                >
                  <i className="fa-regular fa-pen-to-square" aria-hidden />
                  {item.title}
                  {item.date ? <small>{item.date}</small> : null}
                </button>
              ))}
              <button
                type="button"
                className={styles.pill}
                onClick={() => {
                  setCustomOccasionEditId(null);
                  setCustomOccasionSheetOpen(true);
                }}
              >
                <i className="fa-solid fa-plus" aria-hidden />
                Custom Occasion
              </button>
            </div>
          </div>

          <div className={styles.celebrationGroup}>
            <h3 className={styles.groupLabel}>U.S. Holidays</h3>
            <div className={styles.celebrationPills}>
              {attachedHolidayPresets.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.pill} ${styles.pillSelected}`}
                  disabled={!canRemoveAnyHoliday}
                  title={
                    canRemoveAnyHoliday
                      ? "Remove holiday"
                      : "Add another holiday before removing this one"
                  }
                  onClick={() => removeProfileHolidayPreset(item.id)}
                >
                  {item.name}
                  <small>{item.date}</small>
                </button>
              ))}
              <button
                type="button"
                className={styles.pill}
                onClick={openHolidaysMore}
              >
                <i className="fa-solid fa-plus" aria-hidden />
                More
              </button>
            </div>
          </div>
        </section>

        <div className={styles.deleteWrap}>
          <button
            type="button"
            className={styles.deleteButton}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete Account
            <i className="fa-regular fa-trash-can" aria-hidden />
          </button>
        </div>
      </div>
      {customInterestOpen &&
        createPortal(
          <div
            className={wizardStyles.customInterestDrawer}
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-custom-interest-title"
          >
            <button
              type="button"
              className={wizardStyles.customInterestCloseBtn}
              onClick={() => {
                setCustomInterestOpen(false);
                setCustomInterestText("");
              }}
              aria-label="Close custom interest"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div className={wizardStyles.customInterestDrawerBody}>
              <p
                id="profile-custom-interest-title"
                className={wizardStyles.customInterestTitle}
              >
                Add Custom Interest
              </p>
              <input
                type="text"
                className={wizardStyles.customInterestInput}
                value={customInterestText}
                onChange={(e) => setCustomInterestText(e.target.value)}
                placeholder="Interest"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customInterestText.trim())
                    void saveCustomInterest();
                  if (e.key === "Escape") {
                    setCustomInterestOpen(false);
                    setCustomInterestText("");
                  }
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            </div>
            {customInterestText.trim() && (
              <div className={wizardStyles.customInterestActions}>
                <button
                  type="button"
                  className={wizardStyles.customInterestSaveBtn}
                  onClick={() => void saveCustomInterest()}
                >
                  Save
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
      <GifteeInterestsMoreSheet
        open={interestsMoreOpen}
        interestsDraft={interestsDraft}
        onToggleChip={toggleDraftInterestChip}
        onClose={() => setInterestsMoreOpen(false)}
        onSave={saveInterestsMore}
      />
      <GifteeHolidaysMoreSheet
        open={holidaysMoreOpen}
        holidayIdsDraft={holidaysDraft}
        onToggleHoliday={toggleHolidayDraft}
        onClose={() => setHolidaysMoreOpen(false)}
        onSave={saveHolidaysMore}
      />
      <GifteeCustomOccasionSheet
        open={customOccasionSheetOpen}
        editReminder={editingCustomReminder}
        onClose={closeCustomOccasionSheet}
        onSave={(payload) => {
          saveProfileCustomOccasion(payload);
        }}
        onDeleteReminder={
          customOccasionEditId ? deleteProfileCustomReminder : undefined
        }
      />
      {deleteConfirmOpen
        ? createPortal(
            <div
              className={styles.deleteModalRoot}
              role="dialog"
              aria-modal="true"
              aria-labelledby="giftee-delete-profile-dialog-title"
            >
              <button
                type="button"
                className={styles.deleteModalBackdrop}
                onClick={() => setDeleteConfirmOpen(false)}
                aria-label="Close dialog"
              />
              <div className={styles.deleteModalCard}>
                <button
                  type="button"
                  className={styles.deleteModalClose}
                  onClick={() => setDeleteConfirmOpen(false)}
                  aria-label="Close"
                >
                  <i className="fa-solid fa-xmark" aria-hidden />
                </button>
                <div className={styles.deleteModalBody}>
                  <div className={styles.deleteModalCopy}>
                    <p className={styles.deleteModalEyebrow}>Confirm Action</p>
                    <p
                      id="giftee-delete-profile-dialog-title"
                      className={styles.deleteModalTitle}
                    >
                      {deleteConfirmHeadline(
                        firstName,
                        [firstName, lastName].filter(Boolean).join(" ") ||
                          (giftee?.name || "").trim(),
                      )}
                    </p>
                    <p className={styles.deleteModalSub}>
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className={styles.deleteModalActions}>
                    <button
                      type="button"
                      className={styles.deleteModalPrimary}
                      onClick={() => setDeleteConfirmOpen(false)}
                    >
                      Delete Account
                    </button>
                    <button
                      type="button"
                      className={styles.deleteModalSecondary}
                      onClick={() => setDeleteConfirmOpen(false)}
                    >
                      Nevermind
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
      <GifteeAppBottomNav />
    </div>
  );
}
